import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  Typography,
  Space,
  Divider,
  Checkbox,
  Input,
  Modal,
  Row,
  Col,
  Tag,
  Statistic,
  message,
  Spin,
  Alert
} from 'antd';
import {
  PrinterOutlined,
  CheckOutlined,
  ClearOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';
import { useTranslation } from 'react-i18next';
import { sarabunFont } from '../../sarabunFont';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Function to load the Sarabun font into jsPDF
const loadSarabunFont = (doc) => {
  doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
  doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
  doc.setFont('Sarabun');
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat('en-GB', options).format(date).toUpperCase();
};

const formatTime = (timeString) => {
  const [hours, minutes] = timeString.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

const formatNumber = (number) => {
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return '0.00';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parsedNumber);
};

const Registrations = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const sigCanvas = useRef(null);

  // State variables
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentDetails, setStudentDetails] = useState({});
  const [selectedClass, setSelectedClass] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [promotion, setPromotion] = useState('');
  const [price, setPrice] = useState(0);
  const [inventoryPrice, setInventoryPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [memo, setMemo] = useState('');
  const [vat, setVat] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedInventoryItems, setSelectedInventoryItems] = useState([]);
  const [baseClassPrice, setBaseClassPrice] = useState(0);
  const [priceBeforeVat, setPriceBeforeVat] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const formatScheduleHour = (scheduleHourString) => {
    try {
      const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value, 10));
      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hrs')}`;
    } catch (error) {
      console.error('Invalid schedule hour value:', scheduleHourString);
      return 'Invalid Time';
    }
  };

  const calculateEndTime = (startTime, scheduleHour) => {
    const hourParts = scheduleHour.split(' '); 
    const hours = parseInt(hourParts[0], 10);
    const minutes = hourParts.length > 1 ? parseInt(hourParts[1], 10) : 0;
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const startMoment = moment().hours(startHour).minutes(startMinute);
    const endMoment = startMoment.add(hours, 'hours').add(minutes, 'minutes');
    return endMoment.format('HH:mm');
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')} ${t('hrs')}`;
  };

  const fetchStudents = useCallback(async (search = '') => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/students/reg-students-select`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search
        }
      });

      if (response.data.success) {
        setStudents(response.data.students);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      message.error('Failed to fetch students');
    } finally {
      setIsLoading(false);
    }
  }, [API_URL]);

  const fetchClasses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-groups/select`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      message.error('Failed to fetch classes');
    }
  }, [API_URL]);

  const fetchInventoryItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/inventory`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const inventoryData = response.data || [];
      const activeInventoryItems = inventoryData.filter(item => item.status === 'active');
      setInventoryItems(activeInventoryItems);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      message.error('Failed to fetch inventory items');
    }
  }, [API_URL]);

  useEffect(() => {
    fetchStudents();
    fetchClasses();
    fetchInventoryItems();
  }, [fetchStudents, fetchClasses, fetchInventoryItems]);

  const handleStudentSelect = (value) => {
    setSelectedStudent(value);
    const selectedStudentData = students.find(student => student.value === value);
    setStudentDetails(selectedStudentData || {});
  };

  const handleClassSelect = async (value) => {
    setSelectedClass(value);
    const selectedClassData = classes.find(cls => cls.groupid === value);
    const classPrice = selectedClassData ? parseFloat(selectedClassData.price) || 0 : 0;
    setBaseClassPrice(classPrice);
    setPrice(classPrice);
    setPromotion('');
    setInventoryPrice(0);
    const initialPriceBeforeVat = classPrice;
    setPriceBeforeVat(initialPriceBeforeVat);
    calculateTotalPrice(initialPriceBeforeVat, promotion, vat);

    // Fetch full class details for schedule preview
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-groups/${value}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching class schedule:', error);
      setSchedule([]);
    }
  };

  const handleInventorySelect = (selectedOptions) => {
    const selectedItems = selectedOptions.map(itemId => {
      const item = inventoryItems.find(inv => inv.inventory_id === itemId);
      return {
        value: item.inventory_id,
        label: `${item.item_name} (${formatNumber(item.price_per_unit)})`,
        price_per_unit: parseFloat(item.price_per_unit),
        vat: item.vat
      };
    });

    setSelectedInventoryItems(selectedItems);

    const inventoryCost = selectedItems.reduce((total, item) => {
      return total + (parseFloat(item.price_per_unit) || 0);
    }, 0);

    setInventoryPrice(inventoryCost);
    const newPriceBeforeVat = baseClassPrice + inventoryCost - (parseFloat(promotion) || 0);
    setPriceBeforeVat(newPriceBeforeVat);
    calculateTotalPrice(newPriceBeforeVat, promotion, vat);
  };

  const handlePromotionChange = (e) => {
    const promoValue = e.target.value;
    setPromotion(promoValue);
    const parsedPromotion = parseFloat(promoValue) || 0;
    const newPriceBeforeVat = baseClassPrice + inventoryPrice - parsedPromotion;
    setPriceBeforeVat(newPriceBeforeVat);
    calculateTotalPrice(newPriceBeforeVat, promoValue, vat);
  };

  const handleVatChange = (e) => {
    const isChecked = e.target.checked;
    setVat(isChecked);
    calculateTotalPrice(priceBeforeVat, promotion, isChecked);
  };

  const calculateTotalPrice = (priceBeforeVat, promotion, vat) => {
    let finalPrice = priceBeforeVat || 0;
    if (vat) {
      finalPrice = finalPrice * 1.07;
    }
    setTotalPrice(finalPrice);
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleConfirmPayment = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const validPromotion = promotion ? parseFloat(promotion) : 0;
      const validPrice = price ? parseFloat(price) : 0;
      const validTotalPrice = totalPrice ? parseFloat(totalPrice) : 0;

      if (isNaN(validPromotion)) {
        message.error("Invalid promotion value");
        return;
      }

      if (isNaN(validPrice) || validPrice <= 0) {
        message.error("Invalid price value");
        return;
      }

      if (isNaN(validTotalPrice) || validTotalPrice <= 0) {
        message.error("Invalid total price value");
        return;
      }

      const userData = JSON.parse(localStorage.getItem('userData'));
      const userId = userData ? userData.userId : null;

      const signature = sigCanvas.current.isEmpty() ? null : sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      
      const registrationData = {
        studentId: selectedStudent,
        classId: selectedClass,
        promotion: validPromotion,
        finalPrice: validTotalPrice,
        paymentMethod: paymentMethod,
        paymentConfirmed: !!signature,
        createdBy: userId,
        price: validPrice,
        discount: validPromotion,
        signature: signature,
        memo: memo,
        inventoryItems: selectedInventoryItems,
        inventoryPrice: inventoryPrice,
        vat: vat ? 0.07 : 0
      };

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/registrations`, registrationData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success('Registration confirmed successfully!');
      setConfirmationModalOpen(true);

      // Reset form
      form.resetFields();
      setSelectedStudent('');
      setStudentDetails({});
      setSelectedClass('');
      setSchedule([]);
      setPromotion('');
      setPrice(0);
      setTotalPrice(0);
      setPaymentMethod('');
      setMemo('');
      setVat(false);
      setInventoryPrice(0);
      setSelectedInventoryItems([]);
      clearSignature();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving registration:', error);
      message.error('Failed to save registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateSchedulePDF = (schedule) => {
    const doc = new jsPDF();
    loadSarabunFont(doc);
    doc.setFont('Sarabun', 'normal');

    const margin = 20;
    const startY = margin + 60;

    const sortedSchedule = schedule.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (dateA.getTime() === dateB.getTime()) {
        return a.schedule_time.localeCompare(b.schedule_time);
      }
      return dateA - dateB;
    });

    const headerImage = new Image();
    headerImage.src = '/images/Theplanner.png';

    headerImage.onload = () => {
      doc.addImage(headerImage, 'PNG', margin, margin, 35, 30);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('THE PLANNER EDUCATION', margin + 50, margin + 10);

      doc.setFont('helvetica', 'normal');
      doc.text('The Mercury Ville @Chidlom 3rd floor', margin + 50, margin + 15);
      doc.text('Lumpini Pathumwan Bangkok', margin + 50, margin + 20);
      doc.text('www.theplannereducation.com', margin + 50, margin + 25);
      doc.text('Tel. 02-253-2533 or 095-726-2666 Line: @theplanner', margin + 50, margin + 30);

      const classCode = sortedSchedule[0]?.class_code || 'N/A';
      const totalHours = sortedSchedule?.[0]?.total_hours || 0;
      const subjects = [...new Set(sortedSchedule.map(slot => slot.subject_name))];
      const subjectsText = subjects.join(', ');

      const pageWidth = doc.internal.pageSize.getWidth();
      const centerX = pageWidth / 2;
      const maxLineWidth = pageWidth - 40;
      const lineHeight = 5;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${classCode}`, centerX, startY - 15, { align: 'center' });
      doc.text(`Total Hours: ${formatMinutesToHours(totalHours)}`, centerX, startY - 10, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      const splitSubjects = doc.splitTextToSize(`Subject(s): ${subjectsText}`, maxLineWidth);
      const totalSubjectsHeight = splitSubjects.length * lineHeight;

      splitSubjects.forEach((line, index) => {
        doc.text(line, centerX, startY - 5 + (index * lineHeight), { align: 'center' });
      });

      const tableStartY = startY + totalSubjectsHeight;

      doc.setFontSize(18);
      doc.setFont('Sarabun', 'normal');
      doc.text('', 105, startY, { align: 'center' });

      doc.autoTable({
        startY: tableStartY,
        head: [['Day', 'Date', 'Subject Name', 'Start Time', 'End Time', 'Hours', 'Tutor', 'Room']],
        headStyles: {
          fillColor: '#005082',
          textColor: '#FFFFFF',
          fontSize: 10
        },
        body: sortedSchedule.map(slot => [
          slot.schedule_day,
          formatDate(slot.date),
          slot.subject_name,
          formatTime(slot.schedule_time),
          calculateEndTime(slot.schedule_time, slot.schedule_hour),
          formatScheduleHour(slot.schedule_hour),
          slot.nickname,
          slot.classroom_number,
        ]),
        styles: { font: 'Sarabun', fontStyle: 'normal', fontSize: 9 }
      });

      doc.save(`${classCode}.pdf`);
    };
  };

  return (
    <div style={{ 
      marginTop: '80px', 
      marginLeft: '75px', 
      padding: '24px', 
      background: '#f0f2f5', 
      minHeight: '100vh' 
    }}>
      <Sidebar />
      <Header />
      
      <Card>
        <Title level={2} style={{ marginBottom: '24px' }}>
          {t('registration.register_for_course')}
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={() => setIsModalOpen(true)}
        >
          <Row gutter={[24, 16]}>
            {/* Left Column */}
            <Col xs={24} lg={12}>
              <Card title="Student & Class Information" size="small" style={{ marginBottom: '16px' }}>
                <Form.Item
                  label={t('registration.select_student')}
                  name="student"
                  rules={[{ required: true, message: 'Please select a student' }]}
                >
                  <Select
                    showSearch
                    placeholder="Search or select student"
                    loading={isLoading}
                    onSearch={fetchStudents}
                    onChange={handleStudentSelect}
                    filterOption={false}
                    notFoundContent={isLoading ? <Spin size="small" /> : null}
                  >
                    {students.map(student => (
                      <Option key={student.value} value={student.value}>
                        {student.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedStudent && (
                  <Alert
                    message="Student Details"
                    description={
                      <div>
                        <Text><strong>Address:</strong> {studentDetails.address || 'N/A'}</Text><br />
                        <Text><strong>Phone:</strong> {studentDetails.phone || 'N/A'}</Text>
                      </div>
                    }
                    type="info"
                    style={{ marginBottom: '16px' }}
                  />
                )}

                <Form.Item
                  label={t('registration.select_class')}
                  name="class"
                  rules={[{ required: true, message: 'Please select a class' }]}
                >
                  <Select
                    showSearch
                    placeholder={t('registration.select_class')}
                    onChange={handleClassSelect}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {classes.map(cls => (
                      <Option key={cls.groupid} value={cls.groupid}>
                        {`${cls.class_code} ${t('start_date')} ${formatDate(cls.start_date)}`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label={t('registration.select_inventory_items')}
                  name="inventory"
                >
                  <Select
                    mode="multiple"
                    placeholder={t('registration.select_inventory')}
                    onChange={handleInventorySelect}
                  >
                    {inventoryItems.map(item => (
                      <Option key={item.inventory_id} value={item.inventory_id}>
                        {`${item.item_name} (${formatNumber(item.price_per_unit)})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Card>

              {selectedClass && schedule.length > 0 && (
                <Card 
                  title="Class Schedule" 
                  size="small" 
                  style={{ marginBottom: '16px' }}
                  extra={
                    <Button 
                      type="primary" 
                      icon={<PrinterOutlined />}
                      size="small"
                      onClick={() => generateSchedulePDF(schedule)}
                    >
                      Print Schedule
                    </Button>
                  }
                >
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {schedule.map((slot, index) => (
                      <div key={index} style={{ 
                        padding: '8px 0', 
                        borderBottom: index < schedule.length - 1 ? '1px solid #f0f0f0' : 'none' 
                      }}>
                        <Space direction="vertical" size={0}>
                          <Text strong>{formatDate(slot.date)} - {slot.schedule_day}</Text>
                          <Text type="secondary">
                            {formatTime(slot.schedule_time)} ({slot.schedule_hour}) - {slot.subject_name}
                          </Text>
                        </Space>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </Col>

            {/* Right Column */}
            <Col xs={24} lg={12}>
              <Card title="Pricing & Payment" size="small" style={{ marginBottom: '16px' }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title={t('registration.class_price')}
                      value={formatNumber(price)}
                      suffix="THB"
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title={t('registration.inventory_price')}
                      value={formatNumber(inventoryPrice)}
                      suffix="THB"
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Col>
                </Row>

                <Divider />

                <Form.Item
                  label={t('registration.promotion_discount')}
                  name="promotion"
                >
                  <Input
                    type="number"
                    placeholder={t('registration.enter_promotion_value')}
                    value={promotion}
                    onChange={handlePromotionChange}
                    suffix="THB"
                  />
                </Form.Item>

                <Statistic
                  title={t('registration.price_before_vat')}
                  value={formatNumber(priceBeforeVat)}
                  suffix="THB"
                  valueStyle={{ fontSize: '16px', marginBottom: '16px' }}
                />

                <Form.Item name="vat" valuePropName="checked">
                  <Checkbox onChange={handleVatChange}>
                    {t('registration.vat')} {vat && (
                      <Tag color="blue">{formatNumber(priceBeforeVat * 0.07)} THB</Tag>
                    )}
                  </Checkbox>
                </Form.Item>

                <Statistic
                  title={t('registration.total_price')}
                  value={formatNumber(totalPrice)}
                  suffix="THB"
                  valueStyle={{ fontSize: '20px', color: '#1890ff', fontWeight: 'bold' }}
                />
              </Card>

              <Card title="Payment Details" size="small" style={{ marginBottom: '16px' }}>
                <Form.Item
                  label={t('registration.select_payment_method')}
                  name="paymentMethod"
                  rules={[{ required: false, message: 'Please select payment method' }]}
                >
                  <Select
                    placeholder={t('registration.select_payment_method')}
                    onChange={setPaymentMethod}
                  >
                    <Option value="cash">{t('registration.cash')}</Option>
                    <Option value="transfer">{t('registration.transfer')}</Option>
                    <Option value="qr">{t('registration.qr_code')}</Option>
                    <Option value="credit">{t('registration.credit_card')}</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={t('registration.memo')}
                  name="memo"
                >
                  <TextArea
                    rows={3}
                    placeholder={t('registration.enter_additional_notes')}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                  />
                </Form.Item>
              </Card>

              <Card title="Signature Confirmation" size="small" style={{ marginBottom: '16px' }}>
                <div style={{ 
                  border: '1px dashed #d9d9d9', 
                  borderRadius: '6px', 
                  padding: '16px',
                  textAlign: 'center',
                  marginBottom: '8px'
                }}>
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    canvasProps={{ 
                      width: 400, 
                      height: 150, 
                      style: { border: 'none', width: '100%', height: '150px' }
                    }}
                  />
                </div>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={clearSignature}
                  block
                >
                  {t('registration.clear_signature')}
                </Button>
              </Card>

              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  icon={<FileTextOutlined />}
                  onClick={() => setIsModalOpen(true)}
                  disabled={!selectedStudent || !selectedClass}
                  block
                >
                  {t('registration.confirm_registration')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Confirmation Modal */}
      <Modal
        title={t('registration.confirm_registration_details')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            loading={isSubmitting}
            onClick={handleConfirmPayment}
          >
            {t('registration.confirm')}
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <Row gutter={[16, 8]}>
            <Col span={8}><Text strong>Student:</Text></Col>
            <Col span={16}>{studentDetails.firstname} {studentDetails.lastname}</Col>
            
            <Col span={8}><Text strong>Address:</Text></Col>
            <Col span={16}>{studentDetails.address || 'N/A'}</Col>
            
            <Col span={8}><Text strong>Phone:</Text></Col>
            <Col span={16}>{studentDetails.phone || 'N/A'}</Col>
            
            <Col span={8}><Text strong>Class:</Text></Col>
            <Col span={16}>
              {classes.find(cls => cls.groupid === selectedClass) ? 
                `${classes.find(cls => cls.groupid === selectedClass).class_code} - ${classes.find(cls => cls.groupid === selectedClass).subject_name}` : 
                selectedClass
              }
            </Col>
            
            <Col span={8}><Text strong>Price:</Text></Col>
            <Col span={16}>{formatNumber(price)} THB</Col>
            
            <Col span={8}><Text strong>Promotion:</Text></Col>
            <Col span={16}>{promotion || 'None'} THB</Col>
            
            <Col span={8}><Text strong>VAT:</Text></Col>
            <Col span={16}>{vat ? formatNumber(priceBeforeVat * 0.07) : 'N/A'} THB</Col>
            
            <Col span={8}><Text strong>Total:</Text></Col>
            <Col span={16}><Text strong style={{ color: '#1890ff' }}>{formatNumber(totalPrice)} THB</Text></Col>
            
            <Col span={8}><Text strong>Payment:</Text></Col>
            <Col span={16}>{paymentMethod}</Col>
            
            <Col span={8}><Text strong>Memo:</Text></Col>
            <Col span={16}>{memo || 'None'}</Col>
          </Row>

          {sigCanvas.current && !sigCanvas.current.isEmpty() && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Text strong>Signature:</Text>
              <div style={{ marginTop: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '8px' }}>
                <img 
                  src={sigCanvas.current.getTrimmedCanvas().toDataURL('image/png')} 
                  alt="signature" 
                  style={{ maxWidth: '100%', maxHeight: '150px' }} 
                />
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default withRoleAccess(Registrations, ['superadmin', 'admin']);